import { goRequest } from '../utils/httpClientTs';
import {
  UserPersonalInfoResponse,
  UserPersonalInfoUpdateRequest,
  UserAddressUpdateRequest,
  UserRelationshipCreateRequest,
  UserRelationshipUpdateRequest,
  UserCreateRequest,
  UserCreateResponse,
  UserCommitteesResponse,
  TransferMemberRequest,
  InitiateMemberRequest,
  CheckUserResponse,
  InitiateUserRequest,
  MembershipActionRequest,
  MembershipActionCheckResponse,
  UserSearchResponse,
  UserUpdateHistoryRow,
  UserProjectHistoryRow,
  UserPermissionsViewResponse,
} from '../types/users';
import { GenericResponse } from '../types/generic';
import {
  UpsertUserNoteRequest,
  UserNote,
  UserNoteType,
} from '../types/userNotes';

// User Personal Information Endpoints

export const getUserPersonalInfo = (
  userId: string
): Promise<UserPersonalInfoResponse> => {
  return goRequest<UserPersonalInfoResponse>({
    url: `/users/${userId}/personal-info`,
    method: 'GET',
  });
};

export const updateUserPersonalInfo = (
  userId: string,
  data: UserPersonalInfoUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/personal-info`,
    method: 'PATCH',
    data: data,
  });
};

export const createEmailCampaignUnsubscribe = (
  userId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/email-campaign-unsubscribe`,
    method: 'POST',
  });
};

export const deleteEmailCampaignUnsubscribe = (
  userId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/email-campaign-unsubscribe`,
    method: 'DELETE',
  });
};

// User Address Endpoints

export const createUserAddress = (
  userId: string,
  data: UserAddressUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/address`,
    method: 'POST',
    data: data,
  });
};

export const updateUserAddress = (
  userId: string,
  addressId: string,
  data: UserAddressUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/address/${addressId}`,
    method: 'PATCH',
    data: data,
  });
};

export const deleteUserAddress = (
  userId: string,
  addressId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/address/${addressId}`,
    method: 'DELETE',
  });
};

// User Relationship Endpoints

export const createUserRelationship = (
  userId: string,
  data: UserRelationshipCreateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/relationships`,
    method: 'POST',
    data: data,
  });
};

export const updateUserRelationship = (
  userId: string,
  relationshipId: string,
  data: UserRelationshipUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/relationships/${relationshipId}`,
    method: 'PUT',
    data: data,
  });
};

export const deleteUserRelationship = (
  userId: string,
  relationshipId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/relationships/${relationshipId}`,
    method: 'DELETE',
  });
};

// Additional User Endpoints (from existing users.js)

export const searchUsers = (params: {
  search?: string;
  member_status?:
    | 'active'
    | 'inactive'
    | 'deceased'
    | 'non-members'
    | 'all'
    | 'all-members';
  spd_chapter_id?: string;
  local_chapter_id?: string;
  page?: number; // 0-based
  per_page?: number;
}): Promise<UserSearchResponse> => {
  let url = '/users/search';
  const searchParams = new URLSearchParams();

  if (params.search) {
    searchParams.append('search', params.search);
  }
  if (params.member_status && params.member_status !== 'all') {
    searchParams.append('member_status', params.member_status);
  }
  if (params.spd_chapter_id) {
    searchParams.append('spd_chapter_id', params.spd_chapter_id);
  }
  if (params.local_chapter_id) {
    searchParams.append('local_chapter_id', params.local_chapter_id);
  }
  if (params.page !== undefined) {
    searchParams.append('page', params.page.toString());
  }
  if (params.per_page) {
    searchParams.append('per_page', params.per_page.toString());
  }

  if (searchParams.toString()) {
    url += `?${searchParams.toString()}`;
  }

  return goRequest<UserSearchResponse>({
    url: url,
    method: 'GET',
  });
};

/** Current user's application permissions (optional scope: local_chapter_id, spd_chapter_id, international). */
export const getMyPermissions = (params?: {
  local_chapter_id?: string;
  spd_chapter_id?: string;
  international?: string;
}): Promise<string[]> => {
  let url = '/users/me/permissions';
  const searchParams = new URLSearchParams();
  if (params?.local_chapter_id) {
    searchParams.append('local_chapter_id', params.local_chapter_id);
  }
  if (params?.spd_chapter_id) {
    searchParams.append('spd_chapter_id', params.spd_chapter_id);
  }
  if (params?.international) {
    searchParams.append('international', params.international);
  }
  if (searchParams.toString()) {
    url += `?${searchParams.toString()}`;
  }
  return goRequest<string[]>({ url, method: 'GET' });
};

export const advancedSearchUsers = (params: {
  first_name?: string;
  last_name?: string;
  member_id?: string;
  email?: string;
  phone?: string;
  page?: number;
  per_page?: number;
}): Promise<UserSearchResponse> => {
  const searchParams = new URLSearchParams();
  if (params.first_name != null && params.first_name !== '') {
    searchParams.append('first_name', params.first_name);
  }
  if (params.last_name != null && params.last_name !== '') {
    searchParams.append('last_name', params.last_name);
  }
  if (params.member_id != null && params.member_id !== '') {
    searchParams.append('member_id', params.member_id);
  }
  if (params.email != null && params.email !== '') {
    searchParams.append('email', params.email);
  }
  if (params.phone != null && params.phone !== '') {
    searchParams.append('phone', params.phone);
  }
  if (params.page !== undefined) {
    searchParams.append('page', params.page.toString());
  }
  if (params.per_page !== undefined) {
    searchParams.append('per_page', params.per_page.toString());
  }
  const query = searchParams.toString();
  const url = query
    ? `/users/advanced-search?${query}`
    : '/users/advanced-search';
  return goRequest<UserSearchResponse>({ url, method: 'GET' });
};

export const checkUserByEmail = (email: string): Promise<CheckUserResponse> => {
  return goRequest<CheckUserResponse>({
    url: `/users/check?email=${encodeURIComponent(email)}`,
    method: 'GET',
  });
};

export const createUser = (
  data: UserCreateRequest
): Promise<UserCreateResponse> => {
  return goRequest<UserCreateResponse>({
    url: '/users',
    method: 'POST',
    data: data,
  });
};

export const initiateUser = (
  userId: string,
  data: InitiateUserRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/initiate`,
    method: 'POST',
    data: data,
  });
};

// Additional user functions for backward compatibility
export const getUserDetail = (data: { id: string }): Promise<any> => {
  return goRequest<any>({
    url: `/users/${data.id}`,
    method: 'GET',
  });
};

export const getUserPermissionsView = (data: {
  id: string;
}): Promise<UserPermissionsViewResponse> => {
  return goRequest<UserPermissionsViewResponse>({
    url: `/users/${data.id}/permissions/view`,
    method: 'GET',
  });
};

export const getUserCommittees = (data: {
  id: string;
}): Promise<UserCommitteesResponse> => {
  return goRequest<UserCommitteesResponse>({
    url: `/users/${data.id}/committees`,
    method: 'GET',
  });
};

export const getUserNotes = (data: { id: string }): Promise<UserNote[]> => {
  return goRequest<UserNote[]>({
    url: `/users/${data.id}/notes`,
    method: 'GET',
  });
};

export const getUserNoteTypes = (): Promise<UserNoteType[]> => {
  return goRequest<UserNoteType[]>({
    url: '/users/notes/types',
    method: 'GET',
  });
};

export const createUserNote = (data: {
  userId: string;
  payload: UpsertUserNoteRequest;
}): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${data.userId}/notes`,
    method: 'POST',
    data: data.payload,
  });
};

export const updateUserNote = (data: {
  userId: string;
  noteId: string;
  payload: UpsertUserNoteRequest;
}): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${data.userId}/notes/${data.noteId}`,
    method: 'PATCH',
    data: data.payload,
  });
};

export const deleteUserNote = (data: {
  userId: string;
  noteId: string;
}): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${data.userId}/notes/${data.noteId}`,
    method: 'DELETE',
  });
};

export const getUserProjects = (data: {
  id: string;
}): Promise<UserProjectHistoryRow[]> => {
  return goRequest<UserProjectHistoryRow[]>({
    url: `/users/${data.id}/projects`,
    method: 'GET',
  });
};

export const getUserUpdateHistory = (data: {
  id: string;
}): Promise<UserUpdateHistoryRow[]> => {
  return goRequest<UserUpdateHistoryRow[]>({
    url: `/users/${data.id}/update_history`,
    method: 'GET',
  });
};

export const transferPost = (
  data: TransferMemberRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/transfer`,
    method: 'POST',
    data: data,
  });
};

export const initiateMember = (
  data: InitiateMemberRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/initiate`,
    method: 'POST',
    data: data,
  });
};

// Membership Action Endpoints (Transfer/Reinstatement)
export const checkMembershipAction = (
  userId: string,
  data: MembershipActionRequest
): Promise<MembershipActionCheckResponse> => {
  return goRequest<MembershipActionCheckResponse>({
    url: `/users/${userId}/membership-action/check`,
    method: 'POST',
    data: data,
  });
};

export const executeMembershipAction = (
  userId: string,
  data: MembershipActionRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/membership-action`,
    method: 'POST',
    data: data,
  });
};
